import {BehaviorSubject, Observable} from 'rxjs';
import {Controller} from '../Controller';
import {RolesSearchQuery} from '../../common/model/types/roles/RolesSearchQuery';
import {SearchType} from '../../common/model/types/assignees/SearchType';
import {ParentRole, PartialParentRole, PartialRole, Role, SortOrder} from '../../core/rest-client/aat';
import {tAsString} from '../../common/helpers/react/text/tAsString';
import {RxRole} from '../../common/model/db/types/RxRole';
import {DataSource} from '../../common/decorators/rxdb/DataSource';
import {asNumber} from '../../common/helpers/converters/asNumber';
import {DataSourceKind} from '../../common/decorators/rxdb/DataSourceKind';
import {ensure} from '../../common/types/guards/ensure';
import {isNumber} from '../../common/types/guards/isNumber';

export interface PagingState {
    page: number;
    pageSize: number;
    totalRecords: number;
}

export interface SortingState {
    sortBy?: string;
    sortOrder?: SortOrder;
    /**
     * Sorting is delegated to the server, so it is only offered for result sets
     * that are actually fetched through a paged, sortable endpoint.
     */
    supported: boolean;
}

const DEFAULT_PAGE_SIZE = 10;

export class RolesController extends Controller {
    #searchedRoles = new BehaviorSubject<Array<ParentRole> | undefined>([]);
    #visibleRoles = new BehaviorSubject<Array<ParentRole> | undefined>([]);
    #nestedRoles = new BehaviorSubject<Array<Role> | undefined>([]);
    #searchedRolesToAggregate = new BehaviorSubject<Array<ParentRole> | undefined>([]);
    #pagingState = new BehaviorSubject<PagingState>({page: 0, pageSize: DEFAULT_PAGE_SIZE, totalRecords: 0});
    #aggregatePagingState = new BehaviorSubject<PagingState>({page: 0, pageSize: DEFAULT_PAGE_SIZE, totalRecords: 0});
    #lastSearchQuery: RolesSearchQuery | undefined;
    #lastAggregateSearchQuery: RolesSearchQuery | undefined;
    #lastSearchPagingState = new BehaviorSubject<PagingState>({page: 0, pageSize: DEFAULT_PAGE_SIZE, totalRecords: 0});
    #sortingState = new BehaviorSubject<SortingState>({supported: false});
    #aggregateSortingState = new BehaviorSubject<SortingState>({supported: false});
    #viewVersion: number = 0;

    private nextViewVersion(): number {
        return ++this.#viewVersion;
    }

    private isCurrent(version: number): boolean {
        return version === this.#viewVersion;
    }


    getSortingState$(): Observable<SortingState> {
        return this.#sortingState;
    }

    getAggregateSortingState$(): Observable<SortingState> {
        return this.#aggregateSortingState;
    }

    /**
     * Applies server side sorting to the whole result set and reloads the page
     * the user is currently on, so changing the order does not lose their position.
     */
    async changeSorting(sortBy: string | undefined, sortOrder: SortOrder | undefined,
        addAggregatedRolesMode = false): Promise<void> {
        const subject = addAggregatedRolesMode ? this.#aggregateSortingState : this.#sortingState;

        if (!subject.value.supported) {
            return;
        }

        subject.next({sortBy, sortOrder, supported: true});

        const currentPage = addAggregatedRolesMode
            ? this.#aggregatePagingState.value.page
            : this.#pagingState.value.page;

        await this.loadPage(currentPage, addAggregatedRolesMode);
    }

    getLastSearchPagingState(): PagingState {
        return this.#lastSearchPagingState.value;
    }

    showLastSearchedRoles(): void {
        const currentVersion = this.nextViewVersion();
        if (!this.isCurrent(currentVersion)) {
            return;
        }
        this.#pagingState.next(this.#lastSearchPagingState.value);
        this.#visibleRoles.next(this.#searchedRoles.value);
        this.#nestedRoles.next([]);
    }

    clearSearchedRoles(): void {
        this.#searchedRoles.next([]);
        this.#visibleRoles.next([]);
        this.#nestedRoles.next([]);
        this.#lastSearchQuery = undefined;
        this.#sortingState.next({supported: false});
        this.#pagingState.next({page: 0, pageSize: this.#pagingState.value.pageSize, totalRecords: 0});
        this.#lastSearchPagingState.next(this.#pagingState.value);
    }

    clearSearchedRolesToAggregate(): void {
        this.#searchedRolesToAggregate.next([]);
        this.#lastAggregateSearchQuery = undefined;
        this.#aggregateSortingState.next({supported: false});
        this.#aggregatePagingState.next({page: 0, pageSize: this.#aggregatePagingState.value.pageSize, totalRecords: 0});
    }

    @DataSource(DataSourceKind.PARAMETRIZED)
    getRoleById$(id?: string): Observable<RxRole | null> {
        if (id) {
            this.services.roles
                .searchRoleById(asNumber(id))
                .andWeAreDone();

            return this.db.roles
                .getById$(id);
        } else {
            return new BehaviorSubject(null);
        }
    }

    async searchRoles(query: RolesSearchQuery, addAggregatedRolesMode = false): Promise<void> {
        
        const sorting = addAggregatedRolesMode ? this.#aggregateSortingState.value : this.#sortingState.value;

        addAggregatedRolesMode ? this.clearSearchedRolesToAggregate() : this.clearSearchedRoles();

        // A new filter only narrows the result set, the requested order still applies to it.
        const supported = query.type !== SearchType.ROLE_ID;
        if (addAggregatedRolesMode) {
            this.#lastAggregateSearchQuery = query;
            this.#aggregateSortingState.next({...sorting, supported});
        } else {
            this.#lastSearchQuery = query;
            this.#sortingState.next({...sorting, supported});
        }

        if (query.type === SearchType.ROLE_ID) {
            const role = await this.getBusyFlag().run(() => this.services.roles.searchRoleById(parseInt(query.value)), 'SEARCHING_ROLES');
            const result = role ? [role] : [];
            const mapped = result.map(it => ({
                id: it.id,
                name: it.name ?? '',
                notes: it.notes ?? '',
                enabled: true
            }));

            if (addAggregatedRolesMode) {
                this.#searchedRolesToAggregate.next(mapped);
                this.#aggregatePagingState.next({page: 0, pageSize: this.#aggregatePagingState.value.pageSize, totalRecords: mapped.length});
            } else {
                this.#visibleRoles.next(mapped);
                this.#searchedRoles.next(mapped);
                this.#pagingState.next({page: 0, pageSize: this.#pagingState.value.pageSize, totalRecords: mapped.length});
            }
        } else {
            const pagingState = addAggregatedRolesMode ? this.#aggregatePagingState.value : this.#pagingState.value;
            const excludeIds = addAggregatedRolesMode ? await this.computeExcludeIds() : undefined;
            const response = await this.getBusyFlag().run(
                () => this.services.roles.searchRolesPaged(query.value, 0, pagingState.pageSize, excludeIds, sorting.sortBy, sorting.sortOrder),
                'SEARCHING_ROLES'
            );
            const mapped = response.roles.map(it => ({
                id: it.id,
                name: it.name ?? '',
                notes: it.notes ?? '',
                enabled: true
            }));

            if (addAggregatedRolesMode) {
                this.#searchedRolesToAggregate.next(mapped);
                this.#aggregatePagingState.next({page: response.page, pageSize: response.pageSize, totalRecords: response.totalRecords});
            } else {
                this.#visibleRoles.next(mapped);
                this.#searchedRoles.next(mapped);
                this.#pagingState.next({page: response.page, pageSize: response.pageSize, totalRecords: response.totalRecords});
                this.#lastSearchPagingState.next({page: response.page, pageSize: response.pageSize, totalRecords: response.totalRecords});
            }
        }
    }

    async showParentRolesForGivenRole(roleId: number, clickedFromTable = false): Promise<Role | undefined> {
        
        const currentVersion = this.nextViewVersion();
        this.#visibleRoles.next([]);
        this.#sortingState.next({supported: false});
        return await this.getBusyFlag().run(async () => {
            const role = await this.services.roles.searchRoleById(roleId);

            const parentRoles = await this.services.roles.searchParentRolesPaged(
                    roleId, 0, DEFAULT_PAGE_SIZE
                );
           
            if (role) {

                if (!this.isCurrent(currentVersion)) {
                    return;
                }

                if (clickedFromTable) {
                    this.#nestedRoles.next(this.#nestedRoles.value?.filter(r => r.id !== role.id).concat(role));
                } else {
                    const index = (this.#nestedRoles.value?.findIndex(r => r.id === roleId) ?? 0) + 1;
                    this.#nestedRoles.next(this.#nestedRoles.value?.slice(0, index));
                }

                if (!this.isCurrent(currentVersion)) {
                    return;
                }

                if (parentRoles){
                    this.#visibleRoles.next(parentRoles.roles);
                    this.#pagingState.next({page: parentRoles.page, pageSize: parentRoles.pageSize, 
                        totalRecords: parentRoles.totalRecords});
                    this.#sortingState.next({supported: true}); 
                }
            }
            return role;
        }, 'GETTING_PARENT_ROLES');
    }

    async loadPage(page: number, addAggregatedRolesMode = false): Promise<void> {
        const currentVersion = this.nextViewVersion();
        
        if (!addAggregatedRolesMode) {
            this.#visibleRoles.next([]);
        } else {
            this.#searchedRolesToAggregate.next([]);
        }

        const nestedRoles = this.#nestedRoles.value ?? [];
        const pagingState = addAggregatedRolesMode ? this.#aggregatePagingState.value : this.#pagingState.value;
        const sorting = addAggregatedRolesMode ? this.#aggregateSortingState.value : this.#sortingState.value;
        const searchQuery = addAggregatedRolesMode ? this.#lastAggregateSearchQuery : this.#lastSearchQuery;
        const isPagedQuery = !!searchQuery && searchQuery.type !== SearchType.ROLE_ID;

        if (addAggregatedRolesMode) {
            if (!isPagedQuery) {
                return;
            }
            const excludeIds = await this.computeExcludeIds();
            const response = await this.getBusyFlag().run(
                () => this.services.roles.searchRolesPaged(searchQuery!.value, page, pagingState.pageSize, excludeIds, sorting.sortBy, sorting.sortOrder),
                'SEARCHING_ROLES_TO_AGGREGATE'
            );
            if (!this.isCurrent(currentVersion)) {
                return;
            }
            this.#searchedRolesToAggregate.next(response.roles.map(it => ({
                id: it.id, name: it.name ?? '', notes: it.notes ?? '', enabled: true
            })));
            if (!this.isCurrent(currentVersion)) {
                return;
            }
            this.#aggregatePagingState.next({page: response.page, pageSize: response.pageSize, totalRecords: response.totalRecords});
        } else if (nestedRoles.length > 0) {
            const lastRole = nestedRoles[nestedRoles.length - 1];
            if (!lastRole) return;
            ensure(isNumber, lastRole.id);
            const response = await this.getBusyFlag().run(
                () => this.services.roles.searchParentRolesPaged(lastRole.id!, page, pagingState.pageSize, sorting.sortBy, sorting.sortOrder),
                'GETTING_PARENT_ROLES'
            );
            if (!this.isCurrent(currentVersion)) {
                return;
            }
            await this.services.roles.updateDatabase(response.roles);
            if (!this.isCurrent(currentVersion)) {
                return;
            }
            this.#nestedRoles.next(nestedRoles.map((role, index) => index === nestedRoles.length - 1
                ? {...role, parentRoles: response.roles}
                : role
            ));
            this.#visibleRoles.next(response.roles);
            this.#pagingState.next({page: response.page, pageSize: response.pageSize, totalRecords: response.totalRecords});
        } else if (isPagedQuery) {
            const response = await this.getBusyFlag().run(
                () => this.services.roles.searchRolesPaged(searchQuery!.value, page, pagingState.pageSize, undefined, sorting.sortBy, sorting.sortOrder),
                'SEARCHING_ROLES'
            );
            if (!this.isCurrent(currentVersion)) {
                return;
            }
            const mapped = response.roles.map(it => ({
                id: it.id, name: it.name ?? '', notes: it.notes ?? '', enabled: true
            }));
            this.#visibleRoles.next(mapped);
            this.#searchedRoles.next(mapped);
            this.#pagingState.next({page: response.page, pageSize: response.pageSize, totalRecords: response.totalRecords});
            this.#lastSearchPagingState.next(this.#pagingState.value);
        }
    }

    async changePageSize(pageSize: number, addAggregatedRolesMode = false): Promise<void> {
        if (addAggregatedRolesMode) {
            this.#aggregatePagingState.next({...this.#aggregatePagingState.value, pageSize, page: 0});
        } else {
            this.#pagingState.next({...this.#pagingState.value, pageSize, page: 0});
        }
        await this.loadPage(0, addAggregatedRolesMode);
    }

    async computeExcludeIds(): Promise<number[]> {
        const nestedRoles = this.#nestedRoles.value ?? [];
        const lastRole = nestedRoles[nestedRoles.length - 1];
        const visibleParentRolesResponse = await this.services.roles.searchParentRoles(lastRole!.id!); 
        const visibleParentRoleIds = (visibleParentRolesResponse.roles ?? []).map(r => r.id!);
        return [...visibleParentRoleIds, lastRole?.id].filter((id): id is number => id != null);
    }

    async createRole(role: PartialRole): Promise<Role> {
        return await this.getBusyFlag().run(async () => this.services.roles.createRole(role), tAsString('CREATING_ROLE'));
    }

    async updateRole(role: PartialRole): Promise<Role> {
        return await this.getBusyFlag()
            .run(
                async () => {
                    ensure(isNumber, role.id);
                    return this.services.roles.updateRole(role.id, role);
                },
                tAsString('UPDATING_ROLE')
            );
    }

    async updateParentRoles(id: number, parentRoles: PartialParentRole[]): Promise<Role> {
        return await this.getBusyFlag()
            .run(
                async () => this.services.roles.updateParentRoles(id, parentRoles),
                tAsString('UPDATING_PARENT_ROLES')
            );
    }

    async batchAddParentRoles(id: number, parentRoles: PartialParentRole[]): Promise<void> {
        await this.getBusyFlag()
            .run(
                async () => this.services.roles.batchAddParentRoles(id, parentRoles),
                tAsString('UPDATING_PARENT_ROLES')
            );
    }

    async batchRemoveParentRoles(id: number, parentRoleIds: number[]): Promise<void> {
        await this.getBusyFlag()
            .run(
                async () => this.services.roles.batchRemoveParentRoles(id, parentRoleIds),
                tAsString('UPDATING_PARENT_ROLES')
            );
    }

    async batchUpdateParentRolesEnabled(id: number, parentRoleIds: number[], enabled: boolean): Promise<void> {
        await this.getBusyFlag()
            .run(
                async () => this.services.roles.batchUpdateParentRolesEnabled(id, parentRoleIds, enabled),
                tAsString('UPDATING_PARENT_ROLES')
            );
    }

    async reloadCurrentPage(): Promise<void> {
        await this.loadPage(this.#pagingState.value.page);
        if(!this.#visibleRoles.value?.length && this.#pagingState.value.page > 0){
            await this.loadPage(this.#pagingState.value.page-1);
        } 
    }

    getNestedRoles$(): Observable<Array<Role> | undefined> {
        return this.#nestedRoles;
    }

    getVisibleRoles$(): Observable<Array<ParentRole> | undefined> {
        return this.#visibleRoles;
    }

    getSearchedRolesToAggregate$(): Observable<Array<ParentRole> | undefined> {
        return this.#searchedRolesToAggregate;
    }

    getPagingState$(): Observable<PagingState> {
        return this.#pagingState;
    }

    getAggregatePagingState$(): Observable<PagingState> {
        return this.#aggregatePagingState;
    }

    async getParentRoleFromDb(id: number): Promise<PartialParentRole | undefined> {
        return await this.services.roles.getRoleAsParentRole(id.toString());
    }

    async updateParentRoleInDb(role: ParentRole) {
        await this.services.roles.updateDatabase([role]);
    }
}